import React, { FC, useCallback, useEffect, useState } from "react"
import { Title, ExclusiveChoiceDropdown } from "dash-styled"
import { ReactComponent as IconTrash } from "icons/lib/trash.svg"
import { ReactComponent as IconPlus } from "icons/lib/plus.svg"
import { FlatButton } from "../../../../../Common/FlatButton/FlatButton"
import { DownloadSettingsProps } from "./DownloadTypeModal"

const CustomFileNames: FC<{ downloadSettings: DownloadSettingsProps }> = ({
    downloadSettings
}) => {
    const values = downloadSettings.annotationTitles.map(a => a.fieldName)
    const [selectedValues, setSelectedValues] = useState(
        downloadSettings.appDownloadSettings.customFileNameKeys
    )

    const [showNewCustomFieldNameDropdown, setShowNewCustomFieldNameDropdown] =
        useState(selectedValues.length === 0)

    const handleCustomFileNameSelected = useCallback(
        (dropdownIndex: number, dropdownValue: string) => {
            if (selectedValues[dropdownIndex]) {
                selectedValues[dropdownIndex] = dropdownValue
            } else {
                selectedValues.push(dropdownValue)
            }
            setSelectedValues([...selectedValues])
            setShowNewCustomFieldNameDropdown(false)
        },
        [selectedValues]
    )

    useEffect(() => {
        if (
            selectedValues !==
            downloadSettings.appDownloadSettings.customFileNameKeys
        ) {
            downloadSettings.onDownloadSettingsChanged({
                ...downloadSettings.appDownloadSettings,
                customFileNameKeys: selectedValues
            })
        }
    }, [downloadSettings, selectedValues])

    useEffect(() => {
        if (selectedValues.length === 0) setShowNewCustomFieldNameDropdown(true)
    }, [selectedValues.length])

    const handleCustomFileNameDelete = useCallback(() => {
        if (showNewCustomFieldNameDropdown) {
            setShowNewCustomFieldNameDropdown(false)
        } else {
            selectedValues.pop()
            setSelectedValues([...selectedValues])
        }
    }, [selectedValues, showNewCustomFieldNameDropdown])

    const handleRenderChoice = useCallback(
        choice =>
            downloadSettings.annotationTitles.find(v => v.fieldName === choice)!
                .title,
        [downloadSettings.annotationTitles]
    )

    const handleAddButtonClick = useCallback(() => {
        setShowNewCustomFieldNameDropdown(true)
    }, [])

    return (
        <>
            <div className="flex flex-row pb-2 pt-3">
                <Title
                    component="h3"
                    variant="sm"
                    className="flex items-baseline m-0 text-rhino-900"
                >
                    Custom file name
                </Title>
            </div>
            <div className="flex flex-row w-full ml-2 h-6 items-center">
                {selectedValues.map((selectedValue, dropdownIndex) => {
                    const showDash =
                        dropdownIndex < selectedValues.length - 1 ||
                        showNewCustomFieldNameDropdown
                    return (
                        <div
                            className="flex"
                            key={`customFileName-${selectedValue}-${dropdownIndex}`}
                        >
                            <div className="flex flex-col">
                                <ExclusiveChoiceDropdown
                                    choices={values}
                                    selectedIndex={values.findIndex(
                                        v => v === selectedValue
                                    )}
                                    renderChoice={handleRenderChoice}
                                    className="w-full"
                                    inputClassName="h-6"
                                    testId={`custom-file-name-selected-value${dropdownIndex}-dropdown`}
                                    fitContentWidth={true}
                                    size="sm"
                                    onClick={(index: number) =>
                                        handleCustomFileNameSelected(
                                            dropdownIndex,
                                            values[index]
                                        )
                                    }
                                />
                            </div>
                            {showDash && (
                                <div className="flex flex-col mt-auto">
                                    <span>&mdash;</span>
                                </div>
                            )}
                        </div>
                    )
                })}
                <div className="flex">
                    <div className="flex flex-col">
                        {showNewCustomFieldNameDropdown && (
                            <ExclusiveChoiceDropdown
                                choices={values}
                                renderChoice={handleRenderChoice}
                                selectedIndex={-1}
                                placeholder="Select Key"
                                className="w-full"
                                inputClassName="h-6"
                                testId="new-custom-file-name-dropdown"
                                fitContentWidth={true}
                                size="sm"
                                onClick={(index: number) =>
                                    handleCustomFileNameSelected(
                                        selectedValues.length,
                                        values[index]
                                    )
                                }
                            />
                        )}
                    </div>
                </div>

                {selectedValues.length >= 1 && (
                    <div className="flex flex-col">
                        <FlatButton
                            testId="remove-custom-filename-dropdown"
                            className="border-0"
                            onClick={handleCustomFileNameDelete}
                        >
                            <IconTrash />
                        </FlatButton>
                    </div>
                )}

                {selectedValues.length < 3 && !showNewCustomFieldNameDropdown && (
                    <div className="flex flex-col">
                        <FlatButton
                            testId="add-custom-filename-dropdown"
                            className="border-0"
                            onClick={handleAddButtonClick}
                        >
                            <div className="flex flex-row">
                                <div className="flex flex-col">
                                    <IconPlus />
                                </div>
                                <div className="flex flex-col ml-2">Add</div>
                            </div>
                        </FlatButton>
                    </div>
                )}
            </div>
        </>
    )
}

export default CustomFileNames
